import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import TooltipIfDisabled from 'components/common/TooltipIfDisabled';
import Autosuggest from 'components/controls/Autosuggest';
import TableHead, { columnsShape } from 'components/controls/TableHead';
import { connect } from 'formik';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import CloseIcon from 'mdi-react/CloseIcon';
import { array, bool, func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  getRowIdField,
  clearRemovedRow,
  isRowAlreadyAdded,
  removeAddedRow,
  upsertAddedRow,
} from './tableFieldAdded';

const TableField = ({
  onChange = () => {},
  onCheckboxChange = () => {},
  name,
  suggestions,
  value: valueProp = [],
  columns,
  formik,
  buttonText,
  error = false,
  helperText = null,
  removeDisabled = () => false,
  controlsDisabled = () => false,
  disabledTooltip = null,
  placeholder = null,
  loadOptions,
  additional,
  menuPortalTarget,
  menuPosition,
  styles: stylesProp,
}) => {
  const [suggestion, setSuggestion] = useState('');
  const [order, setOrder] = useState({ by: 'name', isReverse: false });
  const sortFunction = useCallback(
    (first, second) => {
      const f = get(first.value, order.by);
      const s = get(second.value, order.by);
      let ret = 0;
      if (typeof f === typeof s && typeof f === 'string') {
        ret = f.localeCompare(s);
      } else if (typeof f === typeof s && typeof f === 'undefined') {
        ret = 0;
      } else if (typeof f === 'undefined') {
        ret = -1;
      } else if (typeof s === 'undefined') {
        ret = 1;
      }
      return order.isReverse ? -ret : ret;
    },
    [order.by, order.isReverse],
  );
  const [value, setValue] = useState(valueProp.sort(sortFunction));
  useEffect(() => {
    const sorted = valueProp.sort(sortFunction);
    setValue(sorted);
  }, [order, sortFunction, valueProp]);
  const handleChange = newSuggestion => {
    setSuggestion(newSuggestion);
  };

  const handleAdd = () => {
    if (!suggestion) return false;
    setSuggestion('');

    // Apply default values for dropdown columns when the current value is not a valid option
    const suggestionWithDefaults = {
      ...suggestion,
      value: { ...suggestion.value },
    };
    columns.forEach(col => {
      if (col.type === 'dropdown' && col.defaultValue !== undefined) {
        const currentValue = suggestionWithDefaults.value[col.key];
        const isValidOption = col.options?.some(opt => opt.value === currentValue);
        if (currentValue === undefined || !isValidOption) {
          suggestionWithDefaults.value[col.key] =
            typeof col.defaultValue === 'function'
              ? col.defaultValue(suggestionWithDefaults.value)
              : col.defaultValue;
        }
      }
    });

    let removed = formik.values[`${name}Removed`];
    const clearedRemoved = clearRemovedRow(removed, suggestionWithDefaults);
    if (clearedRemoved !== removed) {
      removed = clearedRemoved;
      formik.setFieldValue(`${name}Removed`, removed);
    }
    let added = formik.values[`${name}Added`] || [];
    if (!isRowAlreadyAdded(added, suggestionWithDefaults)) {
      added = [...added, suggestionWithDefaults];
      formik.setFieldValue(`${name}Added`, added);
    }
    onChange(name, uniqBy([...value, suggestionWithDefaults]), removed, added);
    return true;
  };

  const handleCheckboxChange = (formikP, nameP, valueP, index, key) => e => {
    const newValue = [...valueP];
    newValue[index].value[key] = e.target.checked;
    formikP.setFieldValue(nameP, newValue);
    onCheckboxChange(formikP, valueP, index, key, e.target.checked);
  };

  const handleSelectChange = (formikP, nameP, valueP, index, key) => e => {
    const newValue = [...valueP];
    const newValueElement = newValue[index];
    newValueElement.value[key] = e.target.value;
    formikP.setFieldValue(nameP, newValue);
    let added = formik.values[`${name}Added`] || [];
    added = upsertAddedRow(added, newValueElement);
    formik.setFieldValue(`${name}Added`, added);
    onCheckboxChange(formikP, valueP, index, key, e.target.value);
  };

  const handleRemove = (formikP, nameP, valueP, index) => () => {
    if (removeDisabled(valueP)) {
      return;
    }
    const newValue = [...valueP];
    const field = getRowIdField(newValue[index]);
    const removedId = newValue[index].value[field];
    newValue.splice(index, 1);
    formikP.setFieldValue(nameP, newValue);
    let removed = formikP.values[`${nameP}Removed`];
    if (!removed.find(t => t === removedId)) {
      removed = [...removed, removedId];
      formikP.setFieldValue(`${nameP}Removed`, removed);
    }
    let added = formikP.values[`${nameP}Added`] || [];
    added = removeAddedRow(added, removedId, field);
    if (added !== formikP.values[`${nameP}Added`]) {
      formikP.setFieldValue(`${nameP}Added`, added);
    }
    onChange(nameP, newValue, removed, added);
  };

  const getComponent = (type, label, val, index, key, options, defaultValue) => {
    switch (type) {
      case 'dropdown':
        return (
          <Select
            variant="standard"
            MenuProps={{ onClick: e => e.stopPropagation() }}
            style={{ width: 100 }}
            value={val[key]}
            onChange={handleSelectChange(formik, name, value, index, key)}
            disabled={controlsDisabled(val)}
            defaultValue={typeof defaultValue === 'function' ? defaultValue(val) : defaultValue}
          >
            {options.map(o => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        );
      case 'checkbox':
        return (
          <Checkbox
            name={`${label}-${key}`}
            checked={!!(val[key] === null || val[key])}
            onChange={handleCheckboxChange(formik, name, value, index, key)}
            color="primary"
            disabled={val[key] === null || controlsDisabled(val)}
          />
        );
      case 'remove':
        return (
          <IconButton
            disabled={removeDisabled(val)}
            onClick={handleRemove(formik, name, value, index)}
          >
            <CloseIcon />
          </IconButton>
        );
      default:
        return null;
    }
  };
  const handleSort = by => {
    if (order.by === by) {
      setOrder({ ...order, isReverse: !order.isReverse });
    } else {
      setOrder({ by, isReverse: false });
    }
  };
  return (
    <Box sx={{ margin: '15px 0 30px 0' }}>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ flexGrow: 1, p: '5px' }}>
          <Autosuggest
            error={error}
            helperText={helperText}
            suggestions={differenceBy(suggestions, value, 'label')}
            value={suggestion}
            onChange={handleChange}
            placeholder={placeholder}
            loadOptions={loadOptions}
            additional={additional}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={stylesProp}
          />
        </Box>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAdd}
          sx={{ m: '5px', minWidth: 100, height: 40 }}
        >
          {buttonText}
        </Button>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          {value.length ? <TableHead order={order} columns={columns} onSort={handleSort} /> : null}
          <TableBody>
            {value.map(({ label, value: val }, index) => {
              return (
                <TableRow
                  key={`${val.id || val._id || Math.random()}`}
                  hover
                  sx={theme => ({
                    border: 0,
                    cursor: 'pointer',
                    '&:nth-of-type(odd)': {
                      backgroundColor: theme.palette.secondary.ultraUltraLight,
                      backgroundImage:
                        'linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1))',
                    },
                  })}
                >
                  {columns.map(({ key, type, options, defaultValue }) => (
                    <TooltipIfDisabled
                      key={key}
                      disabled={type === 'remove' && removeDisabled(val)}
                      title={disabledTooltip}
                    >
                      <TableCell
                        key={`${label}-${key}`}
                        style={type === 'remove' ? { textAlign: 'right' } : undefined}
                      >
                        {getComponent(type, label, val, index, key, options, defaultValue) || (
                          <Typography>{val[key]}</Typography>
                        )}
                      </TableCell>
                    </TooltipIfDisabled>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

TableField.propTypes = {
  onChange: func,
  value: array,
  name: string.isRequired,
  suggestions: array.isRequired,
  columns: columnsShape.isRequired,
  formik: object.isRequired,
  onCheckboxChange: func,
  buttonText: string.isRequired,
  error: bool,
  helperText: string,
  removeDisabled: func,
  controlsDisabled: func,
  disabledTooltip: string,
  placeholder: string,
  loadOptions: func,
  additional: object,
};

export default connect(TableField);
